import Form from "views/Form";
import className from "classnames";
import { toast } from "react-hot-toast";
import { searchHandler } from "models/search";
import Button from "components/UI/Button/Button";
import { dropdownItemSelector } from "models/form";
import { useDispatch, useSelector } from "react-redux";
import { setNavbarTitle } from "features/navbar/navbarSlice";
import { useEffect, useLayoutEffect, useState } from "react";
import { generateRandomNum, parseError } from "utils/helpers";
import { selectAllResults } from "features/search/searchSlice";
import FormElement from "components/Form/FormElement/FormElement";
import { createSearchParams, useNavigate, useParams } from "react-router-dom";
import { formConfig } from "pages/Extensions/NewExtension/formConfig";
import FormDropdown from "components/Form/FormDropdown/FormDropdown";
import { useCreateExtensionMutation } from "features/extension/extensionApiSlice";

import cardStyles from "styles/modules/Card.module.scss";
import formStyles from "components/Form/Form.module.scss";
import utilityStyles from "styles/modules/Utility.module.scss";
import { useGetAccountsByUserQuery } from "features/account/accountApiSlice";
import { useGetCallAccountsByUserQuery } from "features/call_account/callAccountApiSlice";

const form = new Form(formConfig);

const NewExtension = () => {
  const params = useParams();

  const [activeInput, setActiveInput] = useState("");

  const [formElements, setFormElements] = useState([]);
  const [formValidity, setFormValidity] = useState(false);

  const [initialDataValues, setInitialDataValues] = useState([]);

  const [showAccountDetails, setShowAccountDetails] = useState(false);
  const [showCallBillingDetails, setShowCallBillingDetails] = useState(false);

  const [extraFields, setExtraFields] = useState({
    account_number: "",
    account_description: "",
    call_billing_account_name: "",
    call_billing_account_number: "",
    call_billing_account_description: "",
  });

  const searchResults = useSelector(selectAllResults);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { entities, ids } = useGetAccountsByUserQuery({
    userId: params?.id,
    searchParams: "",
  });
  const account = ids?.length > 0 ? entities[ids[0]] : null;

  const { data: callAccount } = useGetCallAccountsByUserQuery({
    userId: params?.id,
    searchParams: "",
  });

  const [createExtension, { isLoading }] = useCreateExtensionMutation();

  useEffect(() => {
    dispatch(setNavbarTitle(params?.id ? "Create User Extension" : "New Extension"));
  }, [dispatch]);

  useLayoutEffect(() => {
    form.createFormElements();
    setFormElements(form.formElements);
  }, []);

  useEffect(() => {
    if (account) {
      const accountData = account;
      const dataValues = [];
      const updatedFormConfig = JSON.parse(JSON.stringify(formConfig));

      Object.entries(accountData).forEach(([key, value]) => {
        if (updatedFormConfig[key]) {
          if (key === "account.number") {
            updatedFormConfig[key].elementConfig.value = accountData.number; // the visible input
            updatedFormConfig[key].elementConfig["data-id"] = accountData.id; // the actual id
          } else {
            updatedFormConfig[key].elementConfig.value = value;
          }
          dataValues.push(String(value));
        }
      });

      form.setConfig(updatedFormConfig);
      form.createFormElements();
      setFormElements(form.formElements);
      setInitialDataValues(dataValues);
    }
  }, [account]);


  useEffect(() => {
    if (callAccount && callAccount.results?.length > 0) {
      const callAccountData = callAccount.results[0];
      const dataValues = [];
      const updatedFormConfig = JSON.parse(JSON.stringify(formConfig));

      Object.entries(callAccountData).forEach(([key, value]) => {
        if (updatedFormConfig[key]) {
          if (key === "call_billing_account_id") {
            updatedFormConfig[key].elementConfig.value = callAccountData.number;
            updatedFormConfig[key].elementConfig["data-id"] = callAccountData.id;
          } else {
            updatedFormConfig[key].elementConfig.value = value;
          }
          dataValues.push(String(value));
        }
      });

      form.setConfig(updatedFormConfig);
      form.createFormElements();
      setFormElements(form.formElements);
      setInitialDataValues(dataValues);
    }
  }, [callAccount]);

  useEffect(() => {
    return () => {
      if (process.env.NODE_ENV === "production") {
        form.clear(); // will run once in production
        form.setConfig(formConfig);
      }
    };
  }, []);

  const submitFormHandler = async (e) => {
    e.preventDefault();

    const formData = {};

    if (formValidity) {
      for (const key in form.getFormConfig()) {
        const el = form.getFormConfig()[key];

        if (key.split("_").pop() === "id") {
          formData[key] = el.elementConfig["data-id"];
        } else {
          formData[key] = el.elementConfig.value;
        }
      }

      try {
        let response = await createExtension(formData).unwrap();
        toast.success("Extension created successfully");

        // navigate(`/extensions/${response?.id}`);
        navigate({
          pathname: `/extensions/${response?.id}`,
          search: createSearchParams({ page: "1" }).toString(),
        });
        // window.location.reload();
      } catch (error) {
        const errorMessage = parseError(error);
        toast.error(errorMessage);
      }
    }
  };

  const dropdownItemClickHandler = (key, item) => {
    dropdownItemSelector(key, item, form);
    setFormElements(form.formElements);
    setActiveInput("");
  };

  const onGenerateCredentials = () => {
    const newFormConfig = JSON.parse(JSON.stringify(formConfig));

    const username = generateRandomNum();

    newFormConfig["username"].elementConfig.value = username.toString();
    newFormConfig["username"].elementConfig.touched = true;

    form.setConfig(newFormConfig);
    form.createFormElements();

    form.checkFormValidity();

    setFormElements(form.formElements);

    setFormValidity(form.isValid);
  };

  const inputChangeHandler = (e) => {
    const { id, value } = e.target;
    setActiveInput(id);

    // Handle form fields that exist in formConfig
    const updatedInput = form.inputChangeHandler(e);

    if (updatedInput) {
      if (e.target.id === "did_id") {
        searchHandler("/dids", e.target.value, dispatch);
      }

      if (e.target.id === "caller_id") {
        searchHandler("/callerids", e.target.value, dispatch);
      }

      if (e.target.id === "account_id") {
        searchHandler("/accounts", e.target.value, dispatch);
      }
      if (e.target.id === "call_billing_account_id") {
        searchHandler("/call_accounts", e.target.value, dispatch);
      }

      form.updateFormElement({ id, config: updatedInput });

      form.checkFormValidity();

      setFormElements(form.formElements);
      setFormValidity(form.isValid);
    }

    // Handle dynamically added fields
    if (extraFields.hasOwnProperty(id)) {
      setExtraFields((prev) => ({ ...prev, [id]: value }));
    }
  };


  const toggleAccountDetails = () => {
    setShowAccountDetails((prev) => !prev);
  };

  const toggleCallBillingDetails = () => {
    setShowCallBillingDetails((prev) => !prev);
  };


  return (
    <div
      className={`${utilityStyles.flexColumn} ${utilityStyles.itemsCenter} ${utilityStyles.justifyCenter}`}
    >
      <div
        className={className({
          [cardStyles.menu]: true,
          [utilityStyles.wFull]: true,
        })}
      >
        <h3>Fill in details for the new extension</h3>
      </div>
      <div className={formStyles.wrapper}>
        <form className={formStyles.container} onSubmit={submitFormHandler}>
          {formElements.map((el, idx) => {
            const { title, elementType, elementConfig, ...config } = el.config;

            return (
              <div key={`el-group-${idx + 1}`} className={formStyles.group}>
                <FormElement
                  {...config}
                  title={title}
                  type={elementType}
                  id={elementConfig.id}
                  config={{
                    ...elementConfig,
                    onChange: inputChangeHandler,
                  }}
                />
                <FormDropdown
                  items={searchResults}
                  itemClickHandler={(item) =>
                    dropdownItemClickHandler(elementConfig.id, item)
                  }
                  visible={activeInput === elementConfig.id && el.config.dropdown}
                />

                {/* Add toggle buttons */}
                {elementConfig.id === "account_id" && (
                  <button
                    type="button"
                    className={formStyles.toggleButton}
                    onClick={toggleAccountDetails}
                  >
                    {showAccountDetails ? "Hide" : "Create Account"}
                  </button>
                )}

                {elementConfig.id === "call_billing_account_id" && (
                  <button
                    type="button"
                    className={formStyles.toggleButton}
                    onClick={toggleCallBillingDetails}
                  >
                    {showCallBillingDetails ? "Hide" : "Create Call Billing Account"}
                  </button>
                )}

                {/* Conditionally render additional fields */}
                {elementConfig.id === "account_id" && showAccountDetails && (
                  <div className={formStyles.groupx}>
                    <FormElement
                      title="Account Number"
                      type="input"
                      id="account_number"
                      config={{
                        id: "account_number",
                        type: "text",
                        value: extraFields.account_number,
                        placeholder: "Enter Account Number",
                        onChange: inputChangeHandler,
                      }}
                    />
                    <FormElement
                      title="Account Description"
                      type="input"
                      id="account_description"
                      config={{
                        id: "account_description",
                        type: "text",
                        value: extraFields.account_description,
                        placeholder: "Enter Account Description",
                        onChange: inputChangeHandler,
                      }}
                    />
                  </div>
                )}

                {elementConfig.id === "call_billing_account_id" && showCallBillingDetails && (
                  <div className={formStyles.groupx}>
                    <FormElement
                      title="Call Billing Account Name"
                      type="input"
                      id="call_billing_account_name"
                      config={{
                        id: "call_billing_account_name",
                        type: "text",
                        value: extraFields.call_billing_account_name,
                        placeholder: "Enter Call Billing Account Name",
                        onChange: inputChangeHandler,
                      }}
                    />
                    <FormElement
                      title="Call Billing Account Number"
                      type="input"
                      id="call_billing_account_number"
                      config={{
                        id: "call_billing_account_number",
                        type: "text",
                        value: extraFields.call_billing_account_number,
                        placeholder: "Enter Call Billing Account Number",
                        onChange: inputChangeHandler,
                      }}
                    />
                    <FormElement
                      title="Call Billing Account Description"
                      type="input"
                      id="call_billing_account_description"
                      config={{
                        id: "call_billing_account_description",
                        type: "text",
                        value: extraFields.call_billing_account_description,
                        placeholder: "Enter Call Billing Account Description",
                        onChange: inputChangeHandler,
                      }}
                    />
                  </div>
                )}
              </div>
            );
          })}

          <div className={formStyles.btn}>
            <button disabled={isLoading || !formValidity}>
              {isLoading ? "Creating extension..." : "Create Extension"}
            </button>
            <Button
              type="button"
              className={`${formStyles.btnRed} ${utilityStyles.ml_1}`}
              onClick={onGenerateCredentials}
            >
              Auto-Generate Username
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewExtension;
